/**
 * ESCHER FALLS — リワード広告の差し口。
 *
 * **このファイルに広告 SDK は入っていない。** 入っているのは境界だけである。
 * AdMob なり何なりを後から `setRewardedAdProvider()` で差し込む。
 *
 * ## なぜ中身を空にしてあるか
 * 実配信には有料の Apple Developer Program と広告アカウントが要る。
 * それが揃う前に SDK を入れると、
 *   - 動かない広告呼び出しがゲームの経路に混じる
 *   - プライバシー申告（「データを収集していません」）が先に嘘になる
 *   - ATT の確認ダイアログだけが出て、見返りが無い
 * という状態で寝かせることになる。境界だけ先に決めておけば、
 * 差すときに触るのはこのファイル1枚で済む。
 *
 * ## 遊びを壊さないための規則
 *
 * 1. **提供者が居なければ、広告の入口を UI に出してはならない。**
 *    `isRewardedAdAvailable()` が false のとき、ボタンそのものを描かないこと。
 *    押しても何も起きないボタンは、壊れているのと同じである。
 *
 * 2. **見返りは「制限時間の延長」だけ。**
 *    ヒント（解ける視点を絞る）を売ってはならない。放水ボタンを解答オラクルに
 *    しなかったのと同じ理由で、盤面を見ずに進める道を作ることになる。
 *    時間はパズルの答えを一切漏らさない、唯一の安全な通貨である。
 *
 * 3. **広告は失敗しうる。** 在庫切れ、通信断、利用者が途中で閉じる。
 *    `show()` が false を返したら**見返りは渡さない**が、
 *    遊びは何事も無かったように続くこと。詰ませてはならない。
 *
 * 4. **買い切りで広告を消したら、提供者ごと外す。**
 *    `setAdsRemoved(true)` を呼べば `isRewardedAdAvailable()` は false になり、
 *    規則1により入口が消える。呼び出し側に分岐は要らない。
 */

/** 1回の視聴で延ばす制限時間 (ms)。 */
export const REWARD_TIME_MS = 30_000;

/**
 * 1つの面で延長を受けられる回数の上限。
 *
 * 無制限にすると、制限時間つきという設計そのものが消える。
 * エクストラを「急かされる面」として残すために、ここは必ず有限にする。
 */
export const REWARD_MAX_PER_LEVEL = 2;

export interface RewardedAdProvider {
  /** いま見せられる広告が用意できているか。準備中や在庫切れなら false。 */
  isReady(): boolean;
  /**
   * 広告を見せる。**最後まで見られたときだけ true** を返すこと。
   * 途中で閉じられた／読み込めなかった場合は false。例外は投げないこと。
   */
  show(): Promise<boolean>;
}

let provider: RewardedAdProvider | null = null;
let adsRemoved = false;

/** 広告の提供者を差し込む。`null` を渡すと外れる（＝入口が消える）。 */
export function setRewardedAdProvider(next: RewardedAdProvider | null): void {
  provider = next;
}

/**
 * 買い切りで広告を消したかを設定する。
 * 課金の実装はまだ無いが、**消せる側の口を先に開けておく**。
 * 後から差すときに、呼び出し側を一切触らずに済む。
 */
export function setAdsRemoved(next: boolean): void {
  adsRemoved = next;
}

export function areAdsRemoved(): boolean {
  return adsRemoved;
}

/** 広告の入口を UI に出してよいか。**出す側は必ずこれを見ること。** */
export function isRewardedAdAvailable(): boolean {
  if (adsRemoved) return false;
  return provider !== null && provider.isReady();
}

/**
 * 広告を見せ、最後まで見られたかを返す。
 * 提供者が居ない・途中で閉じられた・例外が出た、のいずれでも false。
 * **呼び出し側は false を「見返り無し」として静かに扱うこと。**
 */
export async function showRewardedAd(): Promise<boolean> {
  if (!isRewardedAdAvailable() || provider === null) return false;
  try {
    return await provider.show();
  } catch {
    // 広告の失敗でゲームを止めない。ここで握り潰すのが正しい。
    return false;
  }
}
